import { useTranslation } from 'react-i18next';
import { FormGroup, Theme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import AppSlider from '../AppSlider/AppSlider';
import './RoutesFilters.scss';
import { ChangeEvent, useEffect, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import {
  matSwitchStyle,
  matDropDownStyle
} from '../../assets/style/material/matSyles';
import AppSelect from '../AppSelect/AppSelect';
import { usePairingPackage } from '../../providers/PairingPackage';
import { useRef } from 'react';

export default function RoutesFilters({
  displayFiltersClicked,
  setSelectedFilters,
  selectedFilters
}) {
  const AntSwitch = withStyles((theme: Theme) => matSwitchStyle)(Switch);
  const dropDownClasses = matDropDownStyle();

  const { t } = useTranslation();
  const selects = useRef([]);
  const pairingContext = usePairingPackage();

  const [routesFilter, setRoutesFilter] = useState<any>({
    numDays: {
      selected: selectedFilters.selectedRouteFilters.numDays.selected,
      options: ['All']
    },
    landingIn: {
      selected: selectedFilters.selectedRouteFilters.landingIn.selected,
      options: ['All']
    },
    layoverIn: {
      selected: selectedFilters.selectedRouteFilters.layoverIn.selected,
      options: ['All']
    },
    layoverDuration: {
      range: [0, 0],
      selection: selectedFilters.selectedRouteFilters.layoverDuration.selected
    },
    fltNo: selectedFilters.selectedRouteFilters.fltNo,
    sectors: {
      selected: selectedFilters.selectedRouteFilters.sectors.selected,
      options: ['All']
    },
    deadHeadFlight: selectedFilters.selectedRouteFilters.deadHeadFlight,
    redEyeFlights: selectedFilters.selectedRouteFilters.redEyeFlights
  });

  const sliderDefaultState = {
    layoverDuration: routesFilter.layoverDuration
  };
  const stateMapper = new Map();
  stateMapper.set('layoverDuration', 'layOver');

  useEffect(() => {
    if (!pairingContext.pairingPackageData.pairingList?.length) {
      return;
    }

    let sortAsend = (a, b) => (a > b ? 1 : b > a ? -1 : 0);
    let builtState = Object.keys(sliderDefaultState).map((key) => ({
      [key]: (sliderDefaultState[key].range = getMinMax(
        pairingContext.pairingPackageData.pairingList
          .map((p) => p.flightSegments.map((f) => f.layOver))
          .flat(),
        sortAsend
      ))
    }));
    let arrayToObject = builtState.reduce((a, n) => {
      let tempKey = Object.keys(n)[0];
      let defaultObj = { range: n[tempKey], selection: n[tempKey] };
      return { ...a, [tempKey]: defaultObj };
    }, {} as any);

    setRoutesFilter({
      ...routesFilter,
      ...arrayToObject
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairingContext.pairingPackageData]);

  useEffect(() => {
    setRoutesFilter({
      numDays: {
        ...routesFilter.numDays,
        selected: selectedFilters.selectedRouteFilters.numDays.selected
      },
      landingIn: {
        ...routesFilter.landingIn,
        selected: selectedFilters.selectedRouteFilters.landingIn.selected
      },
      layoverIn: {
        ...routesFilter.layoverIn,
        selected: selectedFilters.selectedRouteFilters.layoverIn.selected
      },
      layoverDuration: {
        ...routesFilter.layoverDuration,
        selection:
          selectedFilters.selectedRouteFilters.layoverDuration.selected[0] ===
            0 &&
          selectedFilters.selectedRouteFilters.layoverDuration.selected[1] === 0
            ? routesFilter.layoverDuration.range
            : selectedFilters.selectedRouteFilters.layoverDuration.selected
      },
      fltNo: selectedFilters.selectedRouteFilters.fltNo,
      sectors: {
        ...routesFilter.sectors,
        selected: selectedFilters.selectedRouteFilters.sectors.selected
      },
      deadHeadFlight: selectedFilters.selectedRouteFilters.deadHeadFlight,
      redEyeFlights: selectedFilters.selectedRouteFilters.redEyeFlights
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onChangeSlider = (event: any, newValue: number | number[]) => {
    setRoutesFilter({
      ...routesFilter,
      [event.target.name]: {
        ...routesFilter[event.target.name],
        selection: newValue as number[]
      }
    });
  };

  const getMinMax = (arr, customFunction: any = undefined) => {
    let min = customFunction
      ? arr.slice().sort(customFunction)
      : arr.slice().sort();
    if (!min[0]) {
      min[0] = 0;
    }
    if (!min[arr.length - 1]) {
      min[arr.length - 1] = 0;
    }
    return [min[0], min[arr.length - 1]];
  };

  useEffect(() => {
    if (!pairingContext.pairingPackageData.pairingList?.length) {
      return;
    }

    let numDaysOptions = routesFilter.numDays.options;

    let sectorsOptions = routesFilter.sectors.options;

    let mappedNumDaysOptions =
      pairingContext.pairingPackageData.pairingList.map((f) => f.days);
    let landingInOptions = pairingContext.pairingPackageData.pairingList
      .map((f) => f.departLegs)
      .reduce((a, n) => [...a, ...n], [])
      .filter((f, i, a) => a.indexOf(f) === i);

    let layOverInOptions = pairingContext.pairingPackageData.pairingList
      .map((f) => f.layovers)
      .reduce((a, n) => [...a, ...n], [])
      .filter((f, i, a) => a.indexOf(f) === i);

    let mappedSectorsOptions =
      pairingContext.pairingPackageData.pairingList.map((f) => f.sectors);

    setRoutesFilter({
      ...routesFilter,
      numDays: {
        ...routesFilter.numDays,
        options: [
          ...numDaysOptions,
          ...mappedNumDaysOptions.filter(
            (f, i) => mappedNumDaysOptions.indexOf(f) === i
          )
        ]
      },
      landingIn: {
        ...routesFilter.landingIn,
        options: [...routesFilter.landingIn.options, ...landingInOptions]
      },
      layoverIn: {
        ...routesFilter.layoverIn,
        options: [...routesFilter.layoverIn.options, ...layOverInOptions]
      },
      layoverDuration: {
        ...routesFilter.layoverDuration,
        selection:
          selectedFilters.selectedRouteFilters.layoverDuration.selected[0] ===
            -1 &&
          selectedFilters.selectedRouteFilters.layoverDuration.selected[1] ===
            -1
            ? routesFilter.layoverDuration.range
            : selectedFilters.selectedRouteFilters.layoverDuration.selected
      },
      sectors: {
        ...routesFilter.sectors,
        options: [
          ...sectorsOptions,
          ...mappedSectorsOptions.filter(
            (f, i) => mappedSectorsOptions.indexOf(f) === i
          )
        ]
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairingContext.pairingPackageData]);

  useEffect(() => {
    if (!displayFiltersClicked) {
      return;
    }

    let days = selects.current['days'].querySelector('input').value.trim();
    let landingIn = selects.current['landingIn']
      .querySelector('input')
      .value.trim();
    let layoverIn = selects.current['layoverIn']
      .querySelector('input')
      .value.trim();
    let sectors = selects.current['sectors']
      .querySelector('input')
      .value.trim();
    // TODO: fix this converting back to number
    let daysList = days.split(',').map((f) => Number(f));
    let landingInList = landingIn.split(',');
    let layoverInList = layoverIn.split(',');
    // TODO: fix this converting back to number
    let sectorsList = sectors.split(',').map((f) => Number(f));

    // set the selected filter in pairing package scope
    setSelectedFilters((prevState) => {
      return {
        ...prevState,
        selectedRouteFilters: {
          numDays: {
            selected:
              days.toLowerCase() !== 'all' && !!days ? daysList : ['All']
          },
          landingIn: {
            selected:
              landingIn.toLowerCase() !== 'all' && !!landingIn
                ? landingInList
                : ['All']
          },
          layoverIn: {
            selected:
              layoverIn.toLowerCase() !== 'all' && !!layoverIn
                ? layoverInList
                : ['All']
          },
          layoverDuration: {
            selected: routesFilter.layoverDuration.selection || [-1, -1]
          },
          fltNo: routesFilter.fltNo || '',
          sectors: {
            selected:
              sectors.toLowerCase() !== 'all' && !!sectors
                ? sectorsList
                : ['All']
          },
          deadHeadFlight: routesFilter.deadHeadFlight,
          redEyeFlights: routesFilter.redEyeFlights
        }
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayFiltersClicked]);

  function handleFltNoChange(e: ChangeEvent<HTMLInputElement>): void {
    const fltNo = e.target.value;
    if (fltNo.match(/^(?!0000$)[0-9]{0,4}$/) !== null) {
      setRoutesFilter({ ...routesFilter, fltNo });
    }
  }

  return (
    <div className="routes-filter-container">
      <FormControl className="form-control">
        <AppSelect
          selectLabel={t('filters.routes.numDays')}
          fillElements={routesFilter.numDays.options}
          selectRef={selects}
          selectedItem={routesFilter.numDays.selected}
          multiSelect={true}
          name={'days'}
          selectedStyle={dropDownClasses.selectForRouterFilter}
          sort={true}
        />
      </FormControl>

      <FormControl className="form-control">
        <AppSelect
          selectLabel={t('filters.routes.landingIn')}
          fillElements={routesFilter.landingIn.options}
          selectRef={selects}
          selectedItem={routesFilter.landingIn.selected}
          multiSelect={true}
          name={'landingIn'}
          selectedStyle={dropDownClasses.selectForRouterFilter}
          sort={true}
        />
      </FormControl>

      <div className="routes-filter-container-layover">
        <Typography gutterBottom>
          <span className="routes-filter-layover-label">
            {t('filters.routes.layoverLabel')}
          </span>
        </Typography>
        <FormControl className="form-control">
          <AppSelect
            selectLabel={t('filters.routes.layoverIn')}
            fillElements={routesFilter.layoverIn.options}
            selectRef={selects}
            selectedItem={routesFilter.layoverIn.selected}
            multiSelect={true}
            name={'layoverIn'}
            selectedStyle={dropDownClasses.selectForRouterFilter}
            sort={true}
          />
        </FormControl>
        <AppSlider
          desc={t('filters.routes.layoverDurationDesc')}
          min={routesFilter.layoverDuration.range[0]}
          max={routesFilter.layoverDuration.range[1]}
          value={routesFilter.layoverDuration.selection}
          onChange={onChangeSlider}
          name="layoverDuration"
        />
      </div>

      <FormGroup className="form-control">
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={2}>
            <Grid item xs={6}>
              {t('filters.routes.deadhead')}
            </Grid>
            <Grid item xs={3}>
              <AntSwitch
                className="antSwitch"
                name={'deadHead'}
                checked={routesFilter.deadHeadFlight}
                onClick={(e) =>
                  setRoutesFilter({
                    ...routesFilter,
                    deadHeadFlight: !routesFilter.deadHeadFlight
                  })
                }
              />
            </Grid>
          </Grid>
          <Grid component="label" container alignItems="center" spacing={2}>
            <Grid item xs={6}>
              {t('filters.routes.redeye')}
            </Grid>
            <Grid item xs={3}>
              <AntSwitch
                className="antSwitch"
                name={'redEye'}
                checked={routesFilter.redEyeFlights}
                onClick={(e) =>
                  setRoutesFilter({
                    ...routesFilter,
                    redEyeFlights: !routesFilter.redEyeFlights
                  })
                }
              />
            </Grid>
          </Grid>
        </Typography>
      </FormGroup>

      <FormGroup className="form-control">
        <Typography component="div">
          <input
            name={`fltNo`}
            placeholder={t('filters.routes.fltNoInput')}
            type="text"
            value={routesFilter.fltNo}
            onChange={handleFltNoChange}
            className="fltNo-input"
          />
        </Typography>
      </FormGroup>

      <FormControl className="form-control">
        <AppSelect
          selectLabel={'Sectors'}
          fillElements={routesFilter.sectors.options}
          selectRef={selects}
          selectedItem={routesFilter.sectors.selected}
          multiSelect={true}
          name={'sectors'}
          selectedStyle={dropDownClasses.selectForRouterFilter}
          sort={true}
        />
      </FormControl>
    </div>
  );
}
